<template>
  <video class="shaka" ref="video" autoplay/>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import shaka from 'shaka-player'
import faster from '@/faster'
import appsBuilderAPI from '@/api/apps-builder'

export default {
  name: 'shaka',
  data: () => ({
    player: null,
    videoLoaded: false
  }),
  watch: {
    currentTime: 'updateCurrentTime',
    volume: 'setVolume',
    muted: 'setMuted',
    playing: 'toggle',
    videoId: 'loadVideo'
  },
  computed: {
    ...mapState({
      videoId: state => state.video.id,
      videoFile: state => state.video.file,
      playing: state => state.video.playing,
      volume: state => state.video.audio.volume,
      muted: state => state.video.audio.muted,
      currentTime: state => state.video.timeline.currentTime
    })
  },
  methods: {
    ...mapMutations({ setVideoDuration: 'video/SET_DURATION' }),
    ...mapActions({ trackVideoEvents: 'video/trackEvents' }),
    async loadVideo() {
      this.$refs.video.pause()
      let videoUrl = ''
      if (this.videoFile) {
        videoUrl = this.videoFile
      } else {
        const res = await appsBuilderAPI.getStreamLinkToVideo(this.videoId)
        videoUrl = res.url
      }
      this.player.load(videoUrl)
        .then(() => {
          this.videoLoaded = true
          this.toggle(this.playing)
          this.updateCurrentTime(this.currentTime)
          this.setVolume(this.volume)
          this.setMuted(this.muted)
          this.setVideoDuration(this.$refs.video.duration)

          this.onVideoLoaded && this.onVideoLoaded()
        })
        .catch(this.emitVideoCrash)
    },
    emitVideoCrash(err) {
      faster.emit('video_crash', { videoId: this.videoId, stackTrace: (err || 'unknown').toString() })
    },
    toggle(isPlaying) {
      if (!this.videoLoaded) return

      const video = this.$refs.video
      if (isPlaying && video.paused) {
        video.play()
      } else if (!isPlaying && !video.paused) {
        video.pause()
      }
    },
    updateCurrentTime(currentTime) {
      if (!this.videoLoaded) return

      const video = this.$refs.video
      if (Math.abs(video.currentTime - currentTime) > 0.05) {
        this.$refs.video.currentTime = currentTime
      }
    },
    setVolume(volume) {
      if (!this.videoLoaded) return
      this.$refs.video.volume = volume
    },
    setMuted(muted) {
      if (!this.videoLoaded) return
      this.$refs.video.muted = muted
    }
  },
  async mounted() {
    shaka.polyfill.installAll()
    if (!shaka.Player.isBrowserSupported()) {
      throw new Error('This browser doesn\'t support our video player.')
    }

    const video = this.$refs.video
    this.player = new shaka.Player(video)
    this.player.configure({
      abr: {
        bandwidthDowngradeTarget: 0.95,
        bandwidthUpgradeTarget: 0.75
      },
      streaming: {
        bufferingGoal: 30,
        smallGapLimit: 5,
        jumpLargeGaps: true
      }
    })

    this.player.addEventListener('error', this.emitVideoCrash)
    this.trackVideoEvents({ video, player: this.player })

    await this.loadVideo()
  },
  destroyed() {
    this.player.detach()
  }
}
</script>

<style lang="scss" scoped>
.shaka {
  background: $player-background-color;
  position: fixed;
  left: -3px;
  top: -3px;
  width: calc(100vw + 6px);
  height: calc(100vh + 6px);
}
</style>
